import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/config';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  ClipboardCheck, 
  Camera, 
  Check, 
  X, 
  ChevronDown,
  ChevronUp,
  Save,
  Send,
  ArrowLeft,
  Home,
  Building
} from 'lucide-react';


const AdminSchedaQualitaCompila = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('hw_token');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [property, setProperty] = useState(null);
  const [sections, setSections] = useState([]);
  const [generalNotes, setGeneralNotes] = useState('');
  const [expandedSections, setExpandedSections] = useState({});
  const [existingSheet, setExistingSheet] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  
  const [stats, setStats] = useState({
    total: 0, ok: 0, problems: 0, notChecked: 0, score: 0
  });

  const loadData = useCallback(async () => {
    try {
      const templateRes = await fetch(`${API}/api/quality-sheets/template`);
      const templateData = await templateRes.json();
      
      const sheetRes = await fetch(`${API}/api/quality-sheets/property/${propertyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (sheetRes.ok) {
        const sheetData = await sheetRes.json();
        setProperty({ id: propertyId, name: sheetData.property_name });
        
        if (sheetData.ufficio_sheet) {
          setExistingSheet(sheetData.ufficio_sheet);
          setSections(sheetData.ufficio_sheet.sections);
          setGeneralNotes(sheetData.ufficio_sheet.general_notes || '');
        } else {
          setSections(templateData.sections);
        }
      } else {
        setSections(templateData.sections);
      }
      
      const expanded = {};
      templateData.sections.forEach(s => { expanded[s.id] = true; });
      setExpandedSections(expanded);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, [propertyId, token]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    let total = 0, ok = 0, problems = 0, notChecked = 0;
    
    sections.forEach(section => {
      section.items.forEach(item => {
        total++;
        if (item.checked === true) ok++;
        else if (item.checked === false) problems++;
        else notChecked++;
      });
    });
    
    const score = total > 0 ? Math.round((ok / total) * 100) : 0;
    setStats({ total, ok, problems, notChecked, score });
  }, [sections]);

  const handleCheck = (sectionId, itemId, value) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          items: section.items.map(item => {
            if (item.id === itemId) {
              return { ...item, checked: value };
            }
            return item;
          })
        };
      }
      return section;
    }));
  };

  const handleNoteChange = (sectionId, itemId, note) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          items: section.items.map(item => {
            if (item.id === itemId) {
              return { ...item, note };
            }
            return item;
          })
        };
      }
      return section;
    }));
  };

  const handlePhotoUpload = async (sectionId, itemId, file) => {
    if (!file) return;
    
    setUploadingPhoto(`${sectionId}-${itemId}`);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      await handleSave(false, false);
      
      const res = await fetch(`${API}/api/quality-sheets/property/${propertyId}/photo/${itemId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (res.ok) {
        setSections(prev => prev.map(section => {
          if (section.id === sectionId) {
            return {
              ...section,
              items: section.items.map(item => {
                if (item.id === itemId) {
                  return { 
                    ...item, 
                    photos: [...(item.photos || []), URL.createObjectURL(file)]
                  };
                }
                return item;
              })
            };
          }
          return section;
        }));
        toast.success('Foto caricata');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore upload foto');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Errore upload foto');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handleRemovePhoto = (sectionId, itemId, photoIndex) => {
    setSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          items: section.items.map(item => {
            if (item.id === itemId) {
              const newPhotos = [...(item.photos || [])];
              newPhotos.splice(photoIndex, 1);
              return { ...item, photos: newPhotos };
            }
            return item;
          })
        };
      }
      return section;
    }));
  };

  const handleSave = async (showToast = true, isComplete = false) => {
    setSaving(true);
    
    try {
      const res = await fetch(`${API}/api/quality-sheets/property/${propertyId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          property_id: propertyId,
          sections,
          general_notes: generalNotes,
          is_complete: isComplete
        })
      });
      
      if (res.ok) {
        if (showToast) {
          toast.success(isComplete ? 'Scheda completata!' : 'Bozza salvata');
        }
        return true;
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore salvataggio');
        return false;
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Errore salvataggio');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async () => {
    if (stats.notChecked === stats.total) {
      toast.error('Compila almeno una voce prima di completare');
      return;
    }
    
    if (window.confirm('Sei sicuro di voler completare e firmare la scheda qualità?')) {
      await handleSave(true, true);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ClipboardCheck className="h-6 w-6 text-amber-600" />
              Scheda Qualità - Ufficio
            </h1>
            <p className="text-sm text-slate-500 flex items-center gap-1">
              <Home className="h-4 w-4" />
              {property?.name || 'Immobile'}
            </p>
          </div>
        </div>
        
        {existingSheet?.is_complete && (
          <Badge className="bg-green-100 text-green-800">
            <Check className="h-3 w-3 mr-1" />
            Completata
          </Badge>
        )}
      </div>

      {/* Info */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Building className="h-5 w-5 text-amber-600 mt-0.5" />
            <div className="text-sm text-amber-800">
              <strong>Compila questa scheda</strong> copiando i dati dalla scheda cartacea ricevuta dal Partner.
              Il Responsabile compilerà la sua versione separatamente e poi potrai confrontarle.
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistiche */}
      <Card className="bg-gradient-to-r from-amber-50 to-slate-50">
        <CardContent className="p-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-amber-600">{stats.score}%</div>
              <div className="text-xs text-slate-500">Punteggio</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-green-600">{stats.ok}</div>
              <div className="text-xs text-slate-500">OK</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-600">{stats.problems}</div>
              <div className="text-xs text-slate-500">Problemi</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-slate-400">{stats.notChecked}</div>
              <div className="text-xs text-slate-500">Da verificare</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sezioni */}
      {sections.map(section => (
        <Card key={section.id} className="overflow-hidden">
          <CardHeader 
            className="cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors py-3"
            onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium">{section.title}</CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="text-xs">
                  {section.items.filter(i => i.checked === true).length}/{section.items.length}
                </Badge>
                {expandedSections[section.id] ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
              </div>
            </div>
          </CardHeader>
          
          {expandedSections[section.id] && (
            <CardContent className="p-4 space-y-4">
              {section.items.map(item => (
                <div key={item.id} className="border-b pb-4 last:border-0 last:pb-0">
                  <div className="flex items-start gap-3">
                    <div className="flex gap-2 mt-0.5">
                      <button
                        onClick={() => handleCheck(section.id, item.id, true)}
                        className={`p-1.5 rounded-full transition-colors ${
                          item.checked === true 
                            ? 'bg-green-500 text-white' 
                            : 'bg-slate-100 text-slate-400 hover:bg-green-100'
                        }`}
                        title="OK"
                      >
                        <Check className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleCheck(section.id, item.id, false)}
                        className={`p-1.5 rounded-full transition-colors ${
                          item.checked === false 
                            ? 'bg-red-500 text-white' 
                            : 'bg-slate-100 text-slate-400 hover:bg-red-100'
                        }`}
                        title="Problema"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    
                    <div className="flex-1">
                      <label className={`text-sm ${
                        item.checked === false ? 'text-red-700 font-medium' : ''
                      }`}>
                        {item.label}
                      </label>
                      
                      {(item.checked === false || item.note) && (
                        <Textarea
                          placeholder="Descrivi il problema..."
                          value={item.note || ''}
                          onChange={(e) => handleNoteChange(section.id, item.id, e.target.value)}
                          className="mt-2 text-sm"
                          rows={2}
                        />
                      )}
                      
                      <div className="flex flex-wrap gap-2 mt-2">
                        {(item.photos || []).map((photo, idx) => (
                          <div key={idx} className="relative group">
                            <img 
                              src={photo} 
                              alt={`Foto ${idx + 1}`}
                              className="h-16 w-16 object-cover rounded border"
                            />
                            <button
                              onClick={() => handleRemovePhoto(section.id, item.id, idx)}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                        
                        {(item.photos || []).length < 3 && (
                          <label className="h-16 w-16 border-2 border-dashed rounded flex items-center justify-center cursor-pointer hover:bg-slate-50 transition-colors">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handlePhotoUpload(section.id, item.id, e.target.files[0])}
                              disabled={uploadingPhoto === `${section.id}-${item.id}`}
                            />
                            {uploadingPhoto === `${section.id}-${item.id}` ? (
                              <div className="animate-spin h-4 w-4 border-2 border-amber-600 border-t-transparent rounded-full" />
                            ) : (
                              <Camera className="h-5 w-5 text-slate-400" />
                            )}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          )}
        </Card>
      ))}

      {/* Note generali */}
      <Card>
        <CardHeader className="py-3">
          <CardTitle className="text-base font-medium">Note Generali</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Inserisci eventuali note aggiuntive..."
            value={generalNotes}
            onChange={(e) => setGeneralNotes(e.target.value)}
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Azioni */}
      <div className="flex gap-3 sticky bottom-4 bg-white p-4 rounded-lg shadow-lg border">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => handleSave(true, false)}
          disabled={saving}
        >
          <Save className="h-4 w-4 mr-2" />
          Salva Bozza
        </Button>
        
        <Button
          className="flex-1 bg-amber-600 hover:bg-amber-700"
          onClick={handleComplete}
          disabled={saving || existingSheet?.is_complete}
        >
          <Send className="h-4 w-4 mr-2" />
          {existingSheet?.is_complete ? 'Già Completata' : 'Completa e Firma'}
        </Button>
      </div>

      {/* Firma se completata */}
      {existingSheet?.signature && (
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Check className="h-6 w-6 text-green-600" />
              <div>
                <p className="font-medium text-green-800">Scheda firmata da:</p>
                <p className="text-sm text-green-700">
                  {existingSheet.signature.name} - {new Date(existingSheet.signature.signed_at).toLocaleString('it-IT')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminSchedaQualitaCompila;
